import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import { tagsQueryKeys } from '@/data/services/querykeys';
import { updateTag } from '@/data/services/tagServices';
import Tag from '@/models/Tag';
import { PencilIcon, ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

type TagProps = {
  tag: Tag;
};

export default function TagComponent({ tag }: TagProps) {
  const [isEditing, setIsEditing] = useState(false);

  const queryClient = useQueryClient();

  const { register, handleSubmit } = useForm<Tag>({
    defaultValues: tag,
  });

  const update = async (updatedTag: Tag) => {
    await updateTag(tag.id, updatedTag);
  };

  const { mutate: onUpdate, isLoading: isUpdating } = useMutation(update, {
    onSuccess: () => {
      queryClient.invalidateQueries(
        tagsQueryKeys.list({
          areaId: [tag.areaId],
        }),
      );
    },
    onSettled: () => setIsEditing(false),
  });

  const onSubmit = (e: Tag) => onUpdate(e);

  return (
    <form className="flex flex-col gap-2.5" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex gap-2.5 items-center">
        <ConditionalRenderer
          condition={isEditing}
          fallback={
            <IconButton
              icon={<PencilIcon className="w-5 h-5 text-primary" />}
              onClick={() => setIsEditing(true)}
              key="edit"
              type="button"
            />
          }
        >
          <IconButton
            icon={<ArrowDownOnSquareIcon className="w-5 h-5 text-secondary" />}
            type="submit"
            key="save"
            disabled={isUpdating}
            loadingSize="w-5 h-5 text-primary"
            loading={isUpdating}
          />
        </ConditionalRenderer>
      </div>
      <TextAreaInput
        register={register('description')}
        disabled={!isEditing}
        className={{ base: 'grow', input: 'disabled:text-base-content ' }}
        fontWeight="font-400"
      />
    </form>
  );
}
